import { useEffect, useRef, useState } from 'react';

export default function FinalCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d63nkvpov5qgbon3219g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  return (
    <section id="final-cta" ref={sectionRef} className="relative py-48 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20lawyer%20on%20phone%20call%20in%20modern%20office%2C%20confident%20attorney%20speaking%20on%20mobile%20device%2C%20dramatic%20teal%20color%20grading%20with%20moody%20lighting%2C%20cinematic%20business%20portrait%2C%20dark%20atmospheric%20background%20with%20city%20lights%20bokeh%2C%20professional%20legal%20consultant%2C%20high%20contrast%20noir%20style%20photography%2C%20shallow%20depth%20of%20field%2C%20executive%20business%20environment&width=1920&height=1080&seq=lawyer-phone-003&orientation=landscape"
          alt="Background"
          className="w-full h-full object-cover sm:object-center object-[65%_center]"
        />
        <div className="absolute inset-0 bg-black/20 max-sm:bg-black/30"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,#09090b/50_100%)]"></div>
      </div>

      <div className="relative z-10 max-w-[700px] mx-auto px-5 sm:px-8 text-center">
        <h2
          className={`text-white text-[28px] sm:text-[44px] lg:text-[64px] font-bold leading-tight mb-8 sm:mb-12 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          Join 500+ Attorneys Who Refuse to Compromise.
        </h2>

        <p
          className={`text-[#d4d4d8] text-[16px] sm:text-[20px] mb-10 sm:mb-14 transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          Founder spots are limited. Lock in $25/month before public launch.
        </p>

        <form
          id="waitlist-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className={`flex flex-col sm:flex-row items-stretch max-w-[560px] mx-auto mb-6 gap-3 sm:gap-0 transition-all duration-700 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            disabled={isSubmitting}
            className="flex-1 bg-[#1a1a1d] text-white text-[16px] sm:text-[18px] px-5 sm:px-6 py-4 rounded-xl sm:rounded-l-xl sm:rounded-r-none border-2 border-transparent focus:border-[#06b6d4] focus:outline-none transition-colors disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-[#d4af37] text-black text-[15px] sm:text-[18px] font-bold px-6 sm:px-8 py-4 rounded-xl sm:rounded-l-none sm:rounded-r-xl hover:shadow-[0_0_30px_rgba(212,175,55,0.6)] transition-all duration-200 animate-pulse-glow whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Joining...' : 'Join Founder Waitlist'}
          </button>
        </form>

        {submitStatus === 'success' && (
          <p className="text-[#06b6d4] text-[16px] font-medium animate-fade-in mb-4">
            ✓ Successfully joined the waitlist! Check your email.
          </p>
        )}
        {submitStatus === 'error' && (
          <p className="text-red-400 text-[16px] font-medium animate-fade-in mb-4">
            Please enter a valid email address.
          </p>
        )}

        <p
          className={`text-[#71717a] text-[14px] flex items-center justify-center gap-2 transition-all duration-700 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <i className="ri-lock-fill text-[#06b6d4]"></i>
          No spam. Just your early access link.
        </p>
      </div>

      <style>{`
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 15px rgba(212, 175, 55, 0.3);
          }
          50% {
            box-shadow: 0 0 30px rgba(212, 175, 55, 0.5);
          }
        }
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </section>
  );
}
